import { useQuery } from '@tanstack/react-query';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useEffect, useRef } from 'react';

import { useContentLibraryGateStatus } from '@/components/contentLibrary/ContentLibraryGate';
import { ContentLibraryItemArticle } from '@/components/contentLibrary/contentLibraryItemArticle';
import { ContentLibraryItemVideo } from '@/components/contentLibrary/contentLibraryItemVideo';
import { Header } from '@/components/header';
import { Spinner } from '@/components/ui/spinner';
import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { ROUTES } from '@/constants/routes';
import { useAuthStatus } from '@/hooks/useAuthStatus';
import { useToaster } from '@/hooks/useToaster';
import { getContentLibraryItemV2QueryLikeStatusOptions } from '@/services/getContentLibraryItemLikeStatusV2';
import { getContentLibraryItemV2QueryOptions } from '@/services/getContentLibraryItemV2';
import { useSetContentLibraryItemLikeStatusV2 } from '@/services/setContentLibraryItemLikeStatusV2';
import { type ContentLibraryItem, type LikeStatus } from '@/types/contentLibraryItems';
import { segmentClient } from '@/util/analytics';

export function ContentLibraryItem() {
  const trackedIdRef = useRef<string | null>(null);

  const { id } = useLocalSearchParams<{
    id: string;
  }>();

  const { isAuthenticated, userId } = useAuthStatus();

  const gateStatus = useContentLibraryGateStatus();

  const contentLibraryItemQuery = useQuery(
    getContentLibraryItemV2QueryOptions({ contentLibraryItemId: Number(id) })
  );

  const contentLibraryItemLikeStatusQuery = useQuery({
    ...getContentLibraryItemV2QueryLikeStatusOptions(userId ?? undefined, {
      contentLibraryItemId: Number(id),
    }),
    enabled: isAuthenticated,
  });

  const setLikeStatusMutation = useSetContentLibraryItemLikeStatusV2();

  const contentLibraryItem = contentLibraryItemQuery.data?.contentLibraryItem;

  const likeStatus = contentLibraryItemLikeStatusQuery.data?.likeStatus || null;

  const changeLikeStatus = (newLikeStatus: LikeStatus) => {
    setLikeStatusMutation.mutate({
      contentLibraryItemId: Number(id),
      likeStatus: newLikeStatus,
    });
  };

  const router = useRouter();

  const toast = useToaster();

  useEffect(() => {
    if (contentLibraryItem && trackedIdRef.current !== id) {
      trackedIdRef.current = id;

      segmentClient.track(ANALYTICS_EVENTS.CONTENT_LIBRARY_ITEM_VIEWED, {
        itemId: contentLibraryItem.id,
      });
    }
  }, [id, contentLibraryItem]);

  useEffect(() => {
    if (contentLibraryItemQuery.isError) {
      toast.showWarning({
        title: 'Item not found',
        message: 'The selected Content Library item could not be found.',
      });

      router.replace('/content-library');
    }
  }, [contentLibraryItemQuery.isError]);

  const headerElement = <Header backRoute={ROUTES.CONTENT_LIBRARY} isBackButton />;

  if (contentLibraryItemQuery.isLoading) {
    return (
      <>
        {headerElement}
        <Spinner className="py-24" size="large" />
      </>
    );
  }

  if (!contentLibraryItem) {
    return headerElement;
  }

  // `gateStatus` comes from the wrapping `ContentLibraryGate` (via context, not
  // a second hook call) and is `null` on the `(secure)` route, which renders
  // this component unwrapped. `'attested'` only ever occurs for an
  // unauthenticated visitor — a signed-in user pending NPI verification gets
  // `bypassed`, so this can't unlock the teaser for a route where the item
  // should still be gated by NPI status.
  const isPublic = contentLibraryItem.isPublic === true || gateStatus === 'attested';

  if (contentLibraryItem.type === 'Article') {
    return (
      <ContentLibraryItemArticle
        changeLikeStatus={changeLikeStatus}
        contentLibraryItem={contentLibraryItem}
        isPublic={isPublic}
        likeStatus={likeStatus}
      />
    );
  }

  return (
    <ContentLibraryItemVideo
      changeLikeStatus={changeLikeStatus}
      contentLibraryItem={contentLibraryItem}
      isPublic={isPublic}
      likeStatus={likeStatus}
    />
  );
}
